import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { useLocale } from '~/hooks/useLocale';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { type TimeRange } from './useSchoolBehaviorReportQuery';

const log = createLogger('useStudentBehaviorReportQuery');

export const ADMIN_STUDENT_BEHAVIOR_REPORT_QUERY_KEY = 'adminStudentBehaviorReport';

export type StudentBehaviorSeries = {
  key: string;
  label: string;
  data: (number | null)[];
};

/** Wire shape from `/report/getSchoolBehaviorReport` (per-student). `ticks` are
 *  raw ISO dates; `BehaviorChartView` formats and thins them for display. */
export type StudentBehaviorReportResponse = {
  interval: string;
  ticks: string[];
  series: StudentBehaviorSeries[];
};

export function useStudentBehaviorReportQuery({
  periodId,
  studentId,
  timeRange,
  enabled = true,
}: {
  periodId: string;
  studentId: string;
  timeRange: TimeRange;
  enabled?: boolean;
}) {
  const httpClient = useHttpClient();
  const locale = useLocale();

  const { data, error, status } = useQuery<StudentBehaviorReportResponse>({
    queryKey: [ADMIN_STUDENT_BEHAVIOR_REPORT_QUERY_KEY, periodId, studentId, timeRange],
    queryFn: async () => {
      const tz = locale?.timeZone ?? '';
      log.debug('Fetching student behavior report', { periodId, studentId, timeRange, tz });
      const params = new URLSearchParams({ periodId, studentId, range: timeRange, tz });
      const response = await httpClient.get<StudentBehaviorReportResponse>(`/report/getSchoolBehaviorReport?${params}`);
      log.info('Fetched student behavior report successfully', {
        tickCount: response.ticks.length,
      });
      // Backend returns 0 (not null) for ticks with no absences. The chart pins
      // the absent series to the bottom axis as scatter dots, so emitting a
      // square for every zero produces a solid row across the chart — collapse
      // zeros to null so only real absences render.
      return {
        ...response,
        series: response.series.map((s) =>
          s.key === 'absent' ? { ...s, data: s.data.map((v) => (v === 0 ? null : v)) } : s,
        ),
      };
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch student behavior report', failureCount, error, log }),
    enabled: enabled && httpClient.isAuthenticated && !!periodId && !!studentId,
  });

  return { data, error, status };
}
